import { useData } from 'vike-react/useData';
import {
  ArrowRight,
  Building2,
  FileSearch,
  Gauge,
  Handshake,
  ShieldAlert,
  TrendingDown,
} from 'lucide-react';
import { getAudienceById } from '@shared/constants/audiences';
import { Container } from '../../components/layout/Container';
import { Reveal } from '../../components/motion/Reveal';
import { Button } from '../../components/ui/button';
import { BreadcrumbListLd } from '../../components/seo/jsonld/BreadcrumbList';
import { JourneyTimeline } from '../../components/blocks/JourneyTimeline';
import { ToolsForAudience } from '../../components/blocks/ToolsForAudience';
import { ConcreteCase } from '../../components/blocks/ConcreteCase';
import { SectorHero } from '../../components/sector/SectorHero';
import { SectorProof } from '../../components/sector/SectorProof';
import { RecommendedCeeFiches } from '../../components/sector/RecommendedCeeFiches';
import { SectorFAQ } from '../../components/sector/SectorFAQ';
import { SectorCTA } from '../../components/sector/SectorCTA';
import { getAudienceVoice } from '../../config/audience-voice';
import { audienceLegacyTone } from '../../config/audience-theme';
import type { Data } from './+data';

const AUDIENCE_ID = 'entreprises' as const;

export default function EntreprisesPage() {
  const { journey, tools, cases, ceeFiches } = useData<Data>();
  const audience = getAudienceById(AUDIENCE_ID)!;
  const voice = getAudienceVoice(AUDIENCE_ID);
  const legacyTone = audienceLegacyTone(AUDIENCE_ID);

  return (
    <>
      <BreadcrumbListLd
        items={[
          { name: 'Accueil', url: 'https://solutionecofrance.fr/' },
          { name: 'Entreprises', url: 'https://solutionecofrance.fr/entreprises' },
        ]}
      />

      <SectorHero audience={audience} voice={voice} />

      <SectorProof audience={audience} voice={voice} variant="bento" />

      {journey && (
        <JourneyTimeline
          eyebrow={voice.method.eyebrow}
          title={voice.method.title}
          description={voice.method.description}
          totalDuration={journey.totalDuration}
          ctaLabel={journey.ctaLabel}
          ctaHref={journey.ctaHref}
          steps={journey.steps}
          tone={legacyTone}
        />
      )}

      {tools.length > 0 && (
        <ToolsForAudience
          eyebrow={voice.tools.eyebrow}
          title={voice.tools.title}
          description={voice.tools.description}
          tools={tools.map((t) => ({
            title: t.title,
            description: t.description,
            iconName: t.iconName,
            href: t.href,
            badge: t.badge,
            metric: t.metric,
            metricLabel: t.metricLabel,
          }))}
          tone={legacyTone}
        />
      )}

      <RecommendedCeeFiches audience={audience} fiches={ceeFiches} />

      {cases.length > 0 && (
        <ConcreteCase
          eyebrow={voice.caseStudy.eyebrow}
          title={voice.caseStudy.title}
          cases={cases}
          tone={legacyTone}
        />
      )}

      {/* DECRET TERTIAIRE ALERT — spécifique aux entreprises */}
      <section className="bg-stone-50/50 py-16 md:py-20">
        <Container className="max-w-5xl">
          <Reveal>
            <div className="relative overflow-hidden rounded-2xl border border-accent-200/60 bg-gradient-to-br from-accent-50 via-paper to-ivory-100 p-8 md:p-10">
              <div className="grid gap-8 lg:grid-cols-[1fr_auto] lg:items-center">
                <div className="max-w-2xl">
                  <div className="inline-flex items-center gap-1.5 rounded-md bg-accent-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.16em] text-accent-700">
                    <ShieldAlert className="h-3 w-3" />
                    OPERAT · prochaine échéance 30 septembre
                  </div>
                  <h3 className="mt-4 h-section">
                    Êtes-vous prêt pour la déclaration OPERAT annuelle&nbsp;?
                  </h3>
                  <p className="mt-3 text-sm text-stone-600 leading-relaxed">
                    Tous les bâtiments tertiaires &gt; 1 000 m² doivent déclarer leurs
                    consommations chaque année avant le 30 septembre, et démontrer une
                    trajectoire -40 % à 2030 / -50 % à 2040 / -60 % à 2050. Sanction&nbsp;:
                    amende administrative + publication «&nbsp;name &amp; shame&nbsp;».
                  </p>
                  <Button asChild size="md" variant="primary" className="mt-6 group/btn">
                    <a href="/entreprises/decret-tertiaire">
                      Lancer l&rsquo;audit Décret Tertiaire
                      <ArrowRight className="h-3.5 w-3.5 transition-transform duration-150 ease-out group-hover/btn:translate-x-0.5" />
                    </a>
                  </Button>
                </div>
              </div>
            </div>
          </Reveal>
        </Container>
      </section>

      {/* MULTI-SITES — leviers stratégiques ETI/grands comptes */}
      <section className="bg-paper py-16 md:py-20">
        <Container className="max-w-5xl">
          <Reveal>
            <div className="mx-auto max-w-2xl text-center">
              <p className="eyebrow justify-center">
                <Building2 className="h-3 w-3" strokeWidth={2} />
                Multi-sites & directions énergie
              </p>
              <h2 className="mt-3 h-section">
                Quatre leviers à piloter au niveau du groupe.
              </h2>
              <p className="mt-3 text-[15px] leading-relaxed text-stone-600">
                ETI, grand compte, foncière&nbsp;: un seul interlocuteur pour vos
                comités énergie, vos achats consolidés et vos obligations
                réglementaires. Référent dédié, accord de confidentialité disponible.
              </p>
            </div>
          </Reveal>

          <div className="mt-10 grid gap-4 sm:grid-cols-2">
            {[
              {
                Icon: Gauge,
                eyebrow: 'Conformité',
                title: 'Décret Tertiaire OPERAT',
                description:
                  "Audit, dépôt OPERAT chaque ann\u00e9e, trajectoire \u221240 \u00e0 2030, d\u00e9fense en cas de contr\u00f4le ADEME. Le tout consolid\u00e9 pour tous vos sites.",
                href: '/entreprises/decret-tertiaire',
                ctaLabel: 'Lancer l\u2019audit',
              },
              {
                Icon: TrendingDown,
                eyebrow: 'Courtage HTA',
                title: 'Achat d\u2019\u00e9nergie consolid\u00e9',
                description:
                  "On audite vos tarifs d’acheminement (TURPE, ATRD), on remet l\u2019ensemble en concurrence, on planifie la bascule par lots. Ind\u00e9pendant de chaque fournisseur.",
                href: '/entreprises/comparateur-energie',
                ctaLabel: 'Comparer les offres',
              },
              {
                Icon: FileSearch,
                eyebrow: 'CEE industriels et tertiaires',
                title: 'Mandat CEE multi-sites',
                description:
                  "Fiches BAT et IND valoris\u00e9es au tarif du jour, suramortissement art. 39 quinquies coupl\u00e9 quand c\u2019est pertinent, dossiers couverts par notre RC Pro mandataire.",
                href: '/entreprises/diagnostic-cee',
                ctaLabel: 'Diagnostic CEE',
              },
              {
                Icon: Handshake,
                eyebrow: 'Gouvernance & comités',
                title: 'On parle votre langue',
                description:
                  "Pr\u00e9sentation en comit\u00e9 de direction, supports ISO 50001 compatibles, h\u00e9bergement France\u00a0/\u00a0UE. Accord de confidentialit\u00e9 disponible d\u00e8s le premier \u00e9change.",
                href: '/contact?source=entreprise',
                ctaLabel: 'Prendre rendez-vous',
              },
            ].map(({ Icon, eyebrow, title, description, href, ctaLabel }) => (
              <Reveal key={title}>
                <a
                  href={href}
                  className="group flex h-full flex-col gap-3 rounded-2xl border border-stone-200 bg-paper p-6 transition-all duration-150 ease-out hover:-translate-y-0.5 hover:border-stone-300 hover:shadow-elevated"
                >
                  <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-accent-50 text-accent-700 ring-1 ring-inset ring-accent-200/60">
                    <Icon className="h-4.5 w-4.5" strokeWidth={1.8} />
                  </span>
                  <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-stone-500">
                    {eyebrow}
                  </p>
                  <h3 className="font-display text-[17px] font-bold leading-snug text-ink group-hover:text-accent-800">
                    {title}
                  </h3>
                  <p className="text-[13.5px] leading-relaxed text-stone-600">{description}</p>
                  <span className="mt-auto inline-flex items-center gap-1 text-[12.5px] font-semibold text-accent-700 transition-transform duration-150 ease-out group-hover:translate-x-0.5">
                    {ctaLabel}
                    <ArrowRight className="h-3 w-3" strokeWidth={2.4} />
                  </span>
                </a>
              </Reveal>
            ))}
          </div>
        </Container>
      </section>

      <SectorFAQ audience={audience} voice={voice} />

      <SectorCTA audience={audience} voice={voice} />
    </>
  );
}
